import { useState, useEffect, useCallback } from 'react';
import { api } from '../api/client';
import { useT } from '../i18n';
import type { Project, GridColumn } from '../types';

// Parse a JSONB column that may arrive as an object (pg) or a string
function parseJSON<T>(val: any, fallback: T): T {
  if (!val) return fallback;
  if (typeof val === 'object') return val;
  try { return JSON.parse(val); } catch { return fallback; }
}

// Null-safe short date formatter; returns '' for missing/invalid dates.
function fmtDate(s: string | null | undefined): string {
  if (!s) return '';
  const d = new Date(s);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString();
}

type CouncilTab = 'qualification' | 'backlog';

// Reads the active tab from the hash (#council?tab=backlog) so a shared link
// can point straight at either tab, mirroring GerancePage's parseGeranceHash.
function parseCouncilHash(): CouncilTab {
  const params = new URLSearchParams(window.location.hash.split('?')[1] || '');
  return params.get('tab') === 'backlog' ? 'backlog' : 'qualification';
}

export function CouncilPage() {
  const { t } = useT();
  const [projects, setProjects] = useState<Project[]>([]);
  const [pipeline, setPipeline] = useState<GridColumn[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<CouncilTab>(() => parseCouncilHash());

  // Keep the hash in sync with the open tab (replaceState, not pushState, so
  // switching tabs doesn't spam the back-stack) — the address bar is then
  // always a direct, shareable link back to the tab in view.
  useEffect(() => {
    const newHash = activeTab === 'backlog' ? '#council?tab=backlog' : '#council';
    if (window.location.hash !== newHash) {
      window.history.replaceState(null, '', newHash);
    }
  }, [activeTab]);

  // Resolve a pipeline status id from its label (e.g. 'Planning', 'Backlog')
  const stageId = (label: string) => pipeline.find(c => c.label === label)?.id;

  const reload = useCallback(async () => {
    const [p, cols] = await Promise.all([
      api.getProjects() as Promise<Project[]>,
      api.getGridColumns('pipeline') as Promise<GridColumn[]>,
    ]);
    setProjects(p);
    setPipeline(cols);
    setLoading(false);
  }, []);

  useEffect(() => { reload(); }, [reload]);

  // Move a Qualification project to Planning or Backlog
  const promote = async (id: number, label: string) => {
    const sid = stageId(label);
    if (!sid) return;
    await api.updateProjectStatus(id, sid);
    reload();
  };

  // Toggle a Deferred project's review state
  const setReview = async (id: number, state: string) => {
    await api.setDeferredReview(id, state);
    reload();
  };

  if (loading) {
    return <div className="page-loading"><div className="spinner" /></div>;
  }

  const qualification = projects.filter(p => p.status_label === 'Qualification');
  const backlog = projects.filter(p => p.status_label === 'Backlog');
  const deferred = projects.filter(p => p.status_label === 'Deferred');
  // "To review": oldest deferrals first (those without a date sink to the bottom).
  const toReview = deferred
    .filter(p => p.deferred_review !== 'reviewed')
    .slice()
    .sort((a, b) => {
      if (!a.deferred_at) return 1;
      if (!b.deferred_at) return -1;
      return new Date(a.deferred_at).getTime() - new Date(b.deferred_at).getTime();
    });
  const reviewed = deferred.filter(p => p.deferred_review === 'reviewed');

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">{t('council.title')}</h1>
          <p className="page-meta">{t('council.subtitle')}</p>
        </div>
      </div>

      <div className="tabs">
        <button
          className={`tab${activeTab === 'qualification' ? ' active' : ''}`}
          onClick={() => setActiveTab('qualification')}
          type="button"
        >
          {t('council.tabQualification', { n: qualification.length })}
        </button>
        <button
          className={`tab${activeTab === 'backlog' ? ' active' : ''}`}
          onClick={() => setActiveTab('backlog')}
          type="button"
        >
          {t('council.tabBacklog')}
        </button>
      </div>

      {activeTab === 'qualification' ? (
        qualification.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-title">{t('council.noQualificationTitle')}</div>
            <div className="empty-state-desc">{t('council.noQualificationDesc')}</div>
          </div>
        ) : (
          <div className="flex flex-col gap-8">
            {qualification.map(p => {
              const scoping = parseJSON<any>(p.scoping, {});
              const timeline = scoping.time_horizon_start && scoping.time_horizon_end
                ? `${scoping.time_horizon_start} → ${scoping.time_horizon_end}`
                : '';
              const hours = scoping.total_hours;
              return (
                <div className="card" key={p.id}>
                  <div className="flex items-center gap-8">
                    <span className="font-medium text-ink-1">{p.name}</span>
                    {p.project_code && <span className="badge">{p.project_code}</span>}
                    {p.level_label && <span className="badge">{p.level_label}</span>}
                  </div>
                  {(timeline || hours) && (
                    <div className="text-sm text-ink-3" style={{ marginTop: 'var(--s8)' }}>
                      {timeline && <span>{timeline}</span>}
                      {timeline && hours && <span> · </span>}
                      {hours && <span>{hours}h</span>}
                    </div>
                  )}
                  <div className="flex gap-8" style={{ marginTop: 'var(--s12)' }}>
                    <button className="btn btn-accent btn-sm" onClick={() => promote(p.id, 'Planning')}>{t('council.promoteToPlanning')}</button>
                    <button className="btn btn-ghost btn-sm" onClick={() => promote(p.id, 'Backlog')}>{t('council.moveToBacklog')}</button>
                  </div>
                </div>
              );
            })}
          </div>
        )
      ) : (
        <div className="flex flex-col gap-16">
          {/* ── Backlog ── */}
          <div>
            <h4 className="section-label">{t('council.backlogHeading', { n: backlog.length })}</h4>
            {backlog.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-desc">{t('council.noBacklog')}</div>
              </div>
            ) : (
              <div className="flex flex-col gap-8">
                {backlog.map(p => (
                  <div className="card" key={p.id}>
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-ink-1">{p.name}</span>
                      {p.project_code && <span className="badge">{p.project_code}</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ── Deferred — To review ── */}
          <div>
            <h4 className="section-label">{t('council.deferredToReviewHeading', { n: toReview.length })}</h4>
            {toReview.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-desc">{t('council.noDeferredToReview')}</div>
              </div>
            ) : (
              <div className="flex flex-col gap-8">
                {toReview.map(p => {
                  const deferredOn = fmtDate(p.deferred_at);
                  return (
                  <div className="card" key={p.id}>
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-8">
                        <span className="font-medium text-ink-1">{p.name}</span>
                        {p.project_code && <span className="badge">{p.project_code}</span>}
                      </div>
                      <button className="btn btn-ghost btn-sm" onClick={() => setReview(p.id, 'reviewed')}>{t('council.markReviewed')}</button>
                    </div>
                    {deferredOn && <div className="council-card-dates">{t('council.deferredOn', { date: deferredOn })}</div>}
                  </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* ── Deferred — Reviewed ── */}
          <div>
            <h4 className="section-label">{t('council.deferredReviewedHeading', { n: reviewed.length })}</h4>
            {reviewed.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-desc">{t('council.noDeferredReviewed')}</div>
              </div>
            ) : (
              <div className="flex flex-col gap-8">
                {reviewed.map(p => {
                  const deferredOn = fmtDate(p.deferred_at);
                  const reviewedOn = fmtDate(p.deferred_reviewed_at);
                  return (
                  <div className="card" key={p.id}>
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-8">
                        <span className="font-medium text-ink-1">{p.name}</span>
                        {p.project_code && <span className="badge">{p.project_code}</span>}
                      </div>
                      <button className="btn btn-ghost btn-sm" onClick={() => setReview(p.id, 'to_review')}>{t('council.markToReview')}</button>
                    </div>
                    {(deferredOn || reviewedOn) && (
                      <div className="council-card-dates">
                        {deferredOn && <>{t('council.deferredOn', { date: deferredOn })}</>}
                        {deferredOn && reviewedOn && <> · </>}
                        {reviewedOn && <>{t('council.reviewedOn', { date: reviewedOn })}</>}
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
